import React, { useState } from 'react';
import { Search, Filter, X } from 'lucide-react';

/**
 * Reusable FilterBar component with search and dropdown filters
 * Follows the design pattern from the Users module
 */
const FilterBar = ({ 
  searchTerm, 
  onSearchChange, 
  searchPlaceholder = "Search...",
  filters = [], // Array of filter objects: { label, value, onChange, options }
  onClearFilters,
  children // Additional action buttons (like Create button)
}) => {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Count active filters
  const activeFilterCount = filters.filter(f => f.value && f.value !== '').length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
          
          {/* Filter Button */}
          {filters.length > 0 && (
            <div className="relative">
              <button 
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              
              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Filter Options</h3>
                    
                    {/* Render each filter */}
                    {filters.map((filter, index) => (
                      <div key={index} className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {filter.label}
                        </label>
                        {filter.type === 'select' ? (
                          <select
                            value={filter.value}
                            onChange={(e) => filter.onChange(e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">{filter.placeholder || `All ${filter.label}`}</option>
                            {filter.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : filter.type === 'date' ? (
                          <input
                            type="date"
                            value={filter.value}
                            onChange={(e) => filter.onChange(e.target.value)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        ) : (
                          <input
                            type="text"
                            value={filter.value}
                            onChange={(e) => filter.onChange(e.target.value)}
                            placeholder={filter.placeholder}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        )}
                      </div>
                    ))}
                    
                    {/* Filter Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (onClearFilters) onClearFilters();
                          setShowFilterDropdown(false);
                        }}
                        className="flex-1 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="flex-1 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        {children && (
          <div className="flex gap-2">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterBar;